import type { POI } from "@poi-planner/shared";
import { hasUsableCoordinates } from "./dayMapPois";

export type MapViewportIntent = "fly" | "fit" | "hold";

interface MapViewportIntentInput {
  selectedPoiId: string | null;
  flyToTrigger: number | undefined;
  lastFlyToPoiId: string | null;
  lastFlyToTrigger: number | undefined;
  fitToPoisKey: string | number | undefined;
  lastFitToPoisKey: string | number | undefined;
  fitToPoisSignature: string;
  lastFitToPoisSignature: string | null;
  mapReadyVersion: number;
  lastMapReadyVersion: number;
}

/**
 * Choose exactly one viewport command for a render. Explicit fly requests win;
 * an active selection otherwise holds the viewport until the Day context
 * changes. Deselecting holds the current viewport too — only a genuine
 * context or membership change should re-fit the map.
 */
export function resolveMapViewportIntent({
  selectedPoiId,
  flyToTrigger,
  lastFlyToPoiId,
  lastFlyToTrigger,
  fitToPoisKey,
  lastFitToPoisKey,
  fitToPoisSignature,
  lastFitToPoisSignature,
  mapReadyVersion,
  lastMapReadyVersion,
}: MapViewportIntentInput): MapViewportIntent {
  const mapBecameReady = mapReadyVersion !== lastMapReadyVersion;
  const selectionChanged = selectedPoiId !== lastFlyToPoiId;
  const triggerFired =
    flyToTrigger !== undefined && flyToTrigger !== lastFlyToTrigger;

  if (selectedPoiId && (selectionChanged || triggerFired || mapBecameReady)) {
    return "fly";
  }

  const contextChanged = !Object.is(fitToPoisKey, lastFitToPoisKey);
  const membershipChanged = fitToPoisSignature !== lastFitToPoisSignature;

  if (
    contextChanged ||
    mapBecameReady ||
    (!selectedPoiId && membershipChanged)
  ) {
    return "fit";
  }

  return "hold";
}

/** Stable, order-independent fit identity that also reacts to moved POIs. */
export function mapViewportFitSignature(pois: POI[] | undefined): string {
  return (pois ?? [])
    .filter(hasUsableCoordinates)
    .map((poi) => `${poi.id}:${poi.lat}:${poi.lng}`)
    .sort()
    .join("|");
}
